import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Ruler, Factory, Clock } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import Breadcrumb from "../Breadcrumb";
import FAQSection from "../FAQSection";
import HeroSection from "./HeroSection";          // Same folder
import heroImage from "../assets/industrial-shed2.jpg.png";
import warehouseShed from "../assets/warehouses.png";
import steelWorkshop from "../assets/1.png";
import industrialFramework from "../assets/ind.png";
import prefabOffice from "../assets/pre- engineered metal building(PEMB)1.png";
import factoryShed from "../assets/1.png";
import trussFabrication from "../assets/industrial-shed1.jpg.png";
import storageShed from "../assets/4.png";
import manufacturingUnit from "../assets/2.png";
import siteOffice from "../assets/pre-engineered1.png";
import poultryFarm from "../assets/industrial-structure.jpg";
import { business } from "../../config/business";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const strengths = [
  { icon: ShieldCheck, title: "Site-Specific Engineering", text: "Every structure designed to its own span, height and load requirements." },
  { icon: Ruler, title: "Precision Fabrication", text: "Steel cut, drilled and coated under controlled shop conditions." },
  { icon: Factory, title: "Quality Control", text: "Material and dimensional checks before steel leaves our facility." },
  { icon: Clock, title: "Timely Delivery", text: "Fabrication and foundation work run in parallel for faster completion." },
];

const faqs = [
  { q: "Are these photos from actual completed projects?", a: "Yes — the gallery showcases the range of structures we work on. For specific case studies, see our Projects page." },
  { q: "Can I visit a completed project before deciding?", a: "Depending on client permissions, we can sometimes arrange this — ask us during your project consultation." },
];

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = [
    { id: "all", name: "All Projects" },
    { id: "sheds", name: "Industrial Sheds" },
    { id: "fabrication", name: "Fabrication" },
    { id: "structures", name: "Industrial Structures" },
    { id: "prefab", name: "Prefab Buildings" },
  ];

  const galleryItems = [
    { id: 1, image: warehouseShed, category: "sheds", title: "Warehouse Shed - Hyderabad" },
    { id: 2, image: steelWorkshop, category: "fabrication", title: "Steel Fabrication Workshop" },
    { id: 3, image: industrialFramework, category: "structures", title: "Industrial Framework" },
    { id: 4, image: prefabOffice, category: "prefab", title: "Prefab Office Cabin" },
    { id: 5, image: factoryShed, category: "sheds", title: "Factory Shed Construction" },
    { id: 6, image: trussFabrication, category: "fabrication", title: "Truss Fabrication" },
    { id: 7, image: storageShed, category: "sheds", title: "Storage Shed - Telangana" },
    { id: 8, image: manufacturingUnit, category: "structures", title: "Manufacturing Unit" },
    { id: 9, image: siteOffice, category: "prefab", title: "Site Office Cabin" },
    { id: 10, image: poultryFarm, category: "sheds", title: "Poultry Farm Shed" },
  ];

  const filteredItems =
    selectedCategory === "all"
      ? galleryItems
      : galleryItems.filter((item) => item.category === selectedCategory);

  return (
    <Layout>
      <SEOHead
        title="Gallery | Prefab Buildings, Industrial Sheds & Fabrication"
        description="Explore TR Structures's gallery of prefab buildings, industrial sheds, steel fabrication, pre-engineered buildings, and custom structures showcasing high-quality, durable designs across India."
        keywords="prefab buildings, industrial sheds, steel fabrication, fabrication services, pre-engineered buildings, PEB, prefab gallery, industrial shed gallery, steel fabrication gallery, TR Structures gallery"
      />
      <Breadcrumb items={[{ name: "Gallery" }]} />

      <main>
        {/* 1. Hero */}
        <HeroSection
          title="Our Project Gallery"
          subtitle="A Showcase of Quality & Craftsmanship"
          description="Explore the structures we have proudly delivered across Hyderabad and Telangana. Every project reflects our dedication to precision fabrication and client-focused execution."
          backgroundImage={heroImage}
          primaryButtonText="Get Quote"
          primaryButtonLink="/enquiry"
          secondaryButtonText="Contact Us"
          secondaryButtonLink="/contact"
        />

        {/* 2. Trust strip */}
        <section className="bg-primary py-6">
          <div className="container-custom">
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
              <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Site-specific structural design</span>
              <span className="font-montserrat font-semibold flex items-center gap-2"><Factory size={18} aria-hidden="true" /> Hyderabad fabrication facility</span>
              <span className="font-montserrat font-semibold flex items-center gap-2"><Clock size={18} aria-hidden="true" /> Fast, coordinated delivery</span>
            </div>
          </div>
        </section>

        {/* 3. Filter Buttons - Sticky on scroll */}
        <section className="py-8 bg-muted sticky top-16 z-40 shadow-sm">
          <div className="container-custom">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-montserrat font-semibold transition-all duration-300 shadow-md text-0xl sm:text-base ${
                    selectedCategory === category.id
                      ? "bg-primary text-primary-foreground scale-105"
                      : "bg-card text-foreground hover:bg-primary/10 hover:shadow-lg"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* 4. Gallery Grid */}
        <section className="section-padding">
          <div className="container-custom">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.05 }}
              transition={{ staggerChildren: 0.05 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {filteredItems.map((item) => (
                <motion.div
                  key={item.id}
                  variants={fadeUp}
                  className="group relative overflow-hidden rounded-xl shadow-xl card-hover cursor-pointer"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-gray-200">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                  </div>

                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end">
                    <div className="p-5 text-left transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                      <h3 className="font-montserrat font-bold text-white text-2xl sm:text-lg">
                        {item.title}
                      </h3>
                      <p className="text-white/80 text-2xl sm:text-sm capitalize mt-1">
                        {item.category.replace("-", " ")}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* No results fallback */}
            {filteredItems.length === 0 && (
              <div className="text-center py-20">
                <p className="text-muted-foreground text-lg sm:text-xl">No projects found in this category yet.</p>
              </div>
            )}
          </div>
        </section>

        {/* 5. Why our work stands out */}
        <section className="section-padding bg-muted">
          <div className="container-custom">
            <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-10 text-center">
              What Every Project Reflects
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {strengths.map((s) => (
                <div key={s.title} className="p-6 bg-card rounded-xl shadow-md text-center card-hover">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <s.icon className="text-primary" size={22} aria-hidden="true" />
                  </div>
                  <h3 className="font-montserrat font-bold text-foreground mb-2">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. Related services */}
        <section className="section-padding">
          <div className="container-custom text-center">
            <h2 className="font-montserrat text-2xl font-bold text-foreground mb-8">Explore Our Services</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {[
                { name: "Industrial Sheds", path: "/industrial-sheds" },
                { name: "Steel Fabrication", path: "/structure-steel-fabrication" },
                { name: "Prefab Buildings", path: "/pre-fab-structure-buildings" },
                { name: "Industrial Structures", path: "/IndustrialStructures" },
              ].map((s) => (
                <Link key={s.path} to={s.path} className="p-4 bg-muted rounded-lg font-montserrat font-semibold text-foreground hover:bg-primary hover:text-primary-foreground transition">
                  {s.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* 7. FAQ */}
        <FAQSection faqs={faqs} />

        {/* 8. CTA */}
        <section className="section-padding bg-secondary">
          <div className="container-custom text-center">
            <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-secondary-foreground mb-4">
              Like What You See?
            </h2>
            <p className="text-secondary-foreground/90 mb-8 max-w-xl mx-auto">
              Let's talk about your project and how we can bring the same quality to it.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/enquiry" className="btn-primary inline-block px-8 py-4">
                Get Project Estimate
              </Link>
              <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition">
                <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
              </a>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default Gallery;
